import { useCallback, useEffect, useMemo, useState } from "react";
import FilterBar from "./FilterBar";

const PAGE_SIZE = 60;
function ProductCatalogPicker({
  open,
  products = [],
  selectedIds = [],
  onClose,
  onConfirm,
  onDelinkProducts,
  allowEdits = true,
  linking = false,
  getProductMeta,
  normalizeId,
  currentSupplierId = "",
}) {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("unassigned");
  const [sortKey, setSortKey] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [localSelected, setLocalSelected] = useState(new Set(selectedIds || []));
  const [viewMode, setViewMode] = useState("linkable"); // linkable | linked
  const disableInputs = !allowEdits || linking;

  const normalizedSupplierId = useMemo(() => normalizeId?.(currentSupplierId) || "", [currentSupplierId, normalizeId]);

  useEffect(() => {
    if (!open) return;
    setLocalSelected(new Set(selectedIds || []));
    setQuery("");
    setScope("unassigned");
    setSortKey("name");
    setSortDirection("asc");
    setViewMode("linkable");
    setVisibleCount(PAGE_SIZE);
  }, [open, selectedIds]);

  useEffect(() => {
    // Reset selection when switching views to avoid mixing link and delink actions.
    setLocalSelected(new Set());
    setVisibleCount(PAGE_SIZE);
    if (viewMode === "linkable") {
      setQuery("");
      setScope("unassigned");
    }
  }, [viewMode]);

  const linkableBaseProducts = useMemo(() => {
    const items = Array.isArray(products) ? products : [];
    return items
      .map((prod) => {
        const meta = getProductMeta?.(prod) || {};
        return { meta, prod };
      })
      .filter(({ meta }) => meta.id && normalizeId?.(meta.supplierId) !== normalizedSupplierId);
  }, [getProductMeta, normalizeId, normalizedSupplierId, products]);
  const linkedBaseProducts = useMemo(() => {
    const items = Array.isArray(products) ? products : [];
    return items
      .map((prod) => {
        const meta = getProductMeta?.(prod) || {};
        return { meta, prod };
      })
      .filter(({ meta }) => meta.id && normalizeId?.(meta.supplierId) === normalizedSupplierId);
  }, [getProductMeta, normalizeId, normalizedSupplierId, products]);

  const activeBaseProducts = viewMode === "linked" ? linkedBaseProducts : linkableBaseProducts;

  const counts = useMemo(
    () =>
      linkableBaseProducts.reduce(
        (acc, { meta }) => {
          const hasSupplier = Boolean(normalizeId?.(meta.supplierId));
          if (hasSupplier) {
            acc.assigned += 1;
          } else {
            acc.unassigned += 1;
          }
          return acc;
        },
        { unassigned: 0, assigned: 0 }
      ),
    [linkableBaseProducts, normalizeId]
  );

  const linkScopeOptions = useMemo(
    () => [
      { key: "unassigned", label: `Unassigned (${counts.unassigned})` },
      { key: "assigned", label: `Linked elsewhere (${counts.assigned})` },
      { key: "all", label: `All (${linkableBaseProducts.length})` },
    ],
    [counts.assigned, counts.unassigned, linkableBaseProducts.length]
  );

  const scopedProducts = useMemo(() => {
    if (viewMode === "linked") return activeBaseProducts;
    return activeBaseProducts.filter(({ meta }) => {
      const hasSupplier = Boolean(normalizeId?.(meta.supplierId));
      if (scope === "unassigned") return !hasSupplier;
      if (scope === "assigned") return hasSupplier;
      return true;
    });
  }, [activeBaseProducts, normalizeId, scope, viewMode]);

  const filteredProducts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return scopedProducts;
    return scopedProducts.filter(({ meta }) => {
      const haystack = [meta.name, meta.refCode, meta.barcode]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return haystack.some((value) => value.includes(term));
    });
  }, [query, scopedProducts]);

  const sortedProducts = useMemo(() => {
    const direction = sortDirection === "desc" ? -1 : 1;
    const list = [...filteredProducts];
    const compare = (a, b) => {
      const metaA = a.meta;
      const metaB = b.meta;
      if (sortKey === "refCode") return direction * (metaA.refCode || "").localeCompare(metaB.refCode || "");
      if (sortKey === "barcode") return direction * (metaA.barcode || "").localeCompare(metaB.barcode || "");
      if (sortKey === "assignment") {
        const aHasSupplier = Boolean(normalizeId?.(metaA.supplierId));
        const bHasSupplier = Boolean(normalizeId?.(metaB.supplierId));
        if (aHasSupplier === bHasSupplier) return direction * (metaA.name || "").localeCompare(metaB.name || "");
        return direction * (aHasSupplier ? 1 : -1);
      }
      return direction * (metaA.name || "").localeCompare(metaB.name || "");
    };
    return list.sort(compare);
  }, [filteredProducts, normalizeId, sortDirection, sortKey]);

  const visibleProducts = useMemo(
    () => sortedProducts.slice(0, Math.min(sortedProducts.length, visibleCount)),
    [sortedProducts, visibleCount]
  );
  const hiddenCount = Math.max(sortedProducts.length - visibleProducts.length, 0);

  const allLinkableIds = useMemo(() => new Set(linkableBaseProducts.map(({ meta }) => meta.id)), [linkableBaseProducts]);
  const allFilteredIds = useMemo(() => new Set(sortedProducts.map(({ meta }) => meta.id)), [sortedProducts]);

  const handleListScroll = (event) => {
    if (linking) return;
    const target = event?.target;
    if (!target) return;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;
    if (nearBottom && hiddenCount > 0) {
      setVisibleCount((prev) => Math.min(sortedProducts.length, prev + PAGE_SIZE));
    }
  };

  const isAllFilteredSelected = useMemo(() => {
    if (!sortedProducts.length) return false;
    return sortedProducts.every(({ meta }) => localSelected.has(meta.id));
  }, [localSelected, sortedProducts]);

  const handleToggleSelectAllFiltered = () => {
    if (!sortedProducts.length || disableInputs) return;
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (isAllFilteredSelected) {
        sortedProducts.forEach(({ meta }) => next.delete(meta.id));
      } else {
        sortedProducts.forEach(({ meta }) => {
          if (meta.id) next.add(meta.id);
        });
      }
      return next;
    });
  };

  const toggleSelection = useCallback(
    (productId) => {
      if (disableInputs) return;
      setLocalSelected((prev) => {
        const next = new Set(prev);
        if (next.has(productId)) {
          next.delete(productId);
        } else {
          next.add(productId);
        }
        return next;
      });
    },
    [disableInputs]
  );

  const handleSort = (key) => {
    if (disableInputs) return;
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const handleConfirm = () => {
    if (linking) return;
    const ids = Array.from(localSelected);
    if (viewMode === "linked") {
      if (!onDelinkProducts || !ids.length) return;
      onDelinkProducts(ids);
      return;
    }
    if (!onConfirm) return;
    const sanitized = ids.filter((id) => allLinkableIds.has(id));
    if (!sanitized.length) return;
    onConfirm(sanitized);
  };

  const handleOverlayClick = (event) => {
    if (linking) return;
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="catalog-modal" onClick={(event) => event.stopPropagation()}>
        <header className="catalog-header">
          <div>
            <div className="catalog-title">Add products to supplier</div>
            <div className="muted small">
              Browse full inventory, keep selections across searches, and reassign items linked elsewhere.
            </div>
          </div>
          <div className="catalog-selection">
            <span className="pill status">{localSelected.size} selected</span>
            <button type="button" className="ghost-btn subtle" onClick={onClose} disabled={linking}>
              Close
            </button>
          </div>
        </header>

        <div className="catalog-controls">
          <div className="catalog-search">
            <span aria-hidden="true">🔍</span>
            <input
              type="search"
              placeholder="Search by name, ref code, or barcode"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value || "");
                setVisibleCount(PAGE_SIZE);
              }}
              disabled={disableInputs}
            />
          </div>

          {viewMode === "linked" ? (
            <div />
          ) : (
            <FilterBar options={linkScopeOptions} selected={scope} onSelect={linking ? undefined : setScope} />
          )}

          <div className="catalog-toggle">
            {viewMode === "linked" ? (
              <button type="button" className="ghost-btn small" onClick={() => setViewMode("linkable")} disabled={linking}>
                Back to inventory
              </button>
            ) : (
              <button
                type="button"
                className={`ghost-btn small ${viewMode === "linked" ? "active" : ""}`}
                onClick={() => setViewMode("linked")}
                disabled={linking}
              >
                Show linked to this supplier
              </button>
            )}
          </div>

        </div>

        <div className="catalog-summary muted extra-small">
          {viewMode === "linked"
            ? linkedBaseProducts.length
              ? `${linkedBaseProducts.length} products linked to this supplier. Select and delink as needed.`
              : "No products linked to this supplier."
            : hiddenCount > 0
              ? `Showing ${visibleProducts.length} of ${sortedProducts.length}. Scroll to load ${hiddenCount} more.`
              : sortedProducts.length
                ? `${sortedProducts.length} products match your filters.`
                : "No products match your filters. Adjust filters or search again."}
        </div>

        <div className="catalog-list" onScroll={handleListScroll} aria-busy={linking}>
          <div className="catalog-table-head">
            <label className="catalog-head-cell checkbox">
              <input
                type="checkbox"
                checked={isAllFilteredSelected}
                onChange={handleToggleSelectAllFiltered}
                disabled={disableInputs || !sortedProducts.length}
              />
            </label>
            <button
              type="button"
              className="catalog-head-cell sortable"
              onClick={() => handleSort("name")}
              disabled={disableInputs}
            >
              <span>Name</span>
              <span className="catalog-sort-arrow">{sortKey === "name" ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}</span>
            </button>
            <button
              type="button"
              className="catalog-head-cell sortable"
              onClick={() => handleSort("refCode")}
              disabled={disableInputs}
            >
              <span>Ref code</span>
              <span className="catalog-sort-arrow">
                {sortKey === "refCode" ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
              </span>
            </button>
            <button
              type="button"
              className="catalog-head-cell sortable"
              onClick={() => handleSort("barcode")}
              disabled={disableInputs}
            >
              <span>Barcode</span>
              <span className="catalog-sort-arrow">
                {sortKey === "barcode" ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
              </span>
            </button>
            <button
              type="button"
              className="catalog-head-cell sortable"
              onClick={() => handleSort("assignment")}
              disabled={disableInputs}
            >
              <span>Assignment</span>
              <span className="catalog-sort-arrow">
                {sortKey === "assignment" ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
              </span>
            </button>
          </div>
          {visibleProducts.length ? (
            visibleProducts.map(({ meta }) => {
              const normalizedSupplier = normalizeId?.(meta.supplierId);
              const linkedElsewhere = Boolean(normalizedSupplier && normalizedSupplier !== normalizedSupplierId);
              const status =
                viewMode === "linked"
                  ? "Linked to this supplier"
                  : linkedElsewhere
                    ? `Linked to ${meta.supplierName || "another supplier"}`
                    : "Unassigned";
              const isChecked = localSelected.has(meta.id);
              const displayName =
                (meta.name && String(meta.name).trim()) ||
                (meta.refCode && String(meta.refCode).trim()) ||
                (meta.barcode && String(meta.barcode).trim()) ||
                (meta.id && String(meta.id).trim()) ||
                "Untitled";
              const displayRef = (meta.refCode && String(meta.refCode).trim()) || "—";
              const displayBarcode = (meta.barcode && String(meta.barcode).trim()) || "—";
              return (
                <div key={meta.id} className="catalog-row">
                  <div className="catalog-cell checkbox">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelection(meta.id)}
                      disabled={disableInputs}
                    />
                  </div>
                  <div className="catalog-cell name">
                    <div className="catalog-row__title">{displayName}</div>
                    <div className="catalog-row__hint">{status}</div>
                  </div>
                  <div className="catalog-cell ref">{displayRef}</div>
                  <div className="catalog-cell barcode">{displayBarcode}</div>
                  <div className="catalog-cell status">
                    {viewMode === "linked" ? (
                      <span className="catalog-pill">Linked</span>
                    ) : linkedElsewhere ? (
                      <span className="catalog-pill warn">Reassign</span>
                    ) : (
                      <span className="catalog-pill">Ready</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-hint padded">
              {query.trim() ? "No products match your search." : "No products available in this view."}
            </div>
          )}
        </div>

        <footer className="catalog-footer">
          <div className="catalog-selection-meta muted small">
            {localSelected.size
              ? `${localSelected.size} selected${viewMode === "linked" ? " to delink" : " across filters"}`
              : `Scoped list: ${sortedProducts.length} products`}
          </div>
          <div className="catalog-footer-actions">
            <button
              type="button"
              className="primary-btn"
              onClick={handleConfirm}
              disabled={disableInputs || !localSelected.size}
            >
              {linking
                ? "Processing..."
                : viewMode === "linked"
                  ? `Delink ${localSelected.size || 0} products`
                  : `Link ${localSelected.size || 0} products`}
            </button>
          </div>
        </footer>
        {linking ? (
          <div className="catalog-loading" role="status" aria-live="assertive">
            <div className="catalog-loading__card">
              <div className="catalog-loading__top">
                <span className="catalog-loading__dot" aria-hidden="true" />
                <div className="catalog-loading__title">
                  {viewMode === "linked" ? "Delinking products..." : "Linking products..."}
                </div>
              </div>
              <div className="catalog-loading__hint">
                {localSelected.size
                  ? `${localSelected.size} item${localSelected.size === 1 ? "" : "s"} in progress.`
                  : "Requests are still running. Hang tight while we finish."}
              </div>
              <div className="catalog-loading__bar" aria-hidden="true">
                <div className="catalog-loading__bar-fill" />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ProductCatalogPicker;
